import { forwardRef } from "react";
import Link from "next/link";

import { Anchor, Button } from "@newsela/angelou";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";

import BaseCard from "@/components/BaseCard";
import AssignModal from "@/components/AssignModal";
import AdminContentSettingsCardFooter from "@/admin/AdminContentSettingsCardFooter";

import { useContentCard } from "../hooks/useContentCard";

import {
  ArticleCardMetadata,
  CardViewmodelAndProps,
} from "../ContentCardV2.interface";
import {
  ARTICLE_STORY_MAP,
  CONTENT_TYPES_LABELS,
} from "../ContentCardV2.constants";
import { useFocusRestoringModal } from "../ContentCardV2.utils";

import {
  addToAssignmentButtonCss,
  cardActionsContainerCss,
  contentSettingsModeCss,
  linkCss,
} from "./styles";

const AnchorWithTrackEvent = withTrackEvent(Anchor);
const ButtonWithTrackEvent = withTrackEvent(Button);

export const ArticleCard = forwardRef<HTMLElement, CardViewmodelAndProps>(
  (props, ref) => {
    const {
      id: contentId,
      contentSettingsGroups,
      dataQaSelector = "",
      display_date: displayDate,
      images,
      isContentSettingsEnabled,
      metadata,
      title,
      url,
      visibility_markers: visibilityMarkers,
    } = props;

    const {
      article_type: articleType = "",
      content_type: contentType,
      date_lite_product_grant_expires: dateLiteProductGrantExpires = "",
      languages,
      maturity_range: maturityRange,
      lexile_levels: lexileLevels,
    } = metadata as ArticleCardMetadata;

    const {
      assignHref,
      badges,
      createAssignmentUrl,
      formattedMaturityGradeLevelLabel,
      formattedReadingLevelLabel,
      showAssignmentAnchor,
    } = useContentCard({
      contentId,
      contentType,
      dateLiteProductGrantExpires,
      maturityRange,
      lexileLevels,
      visibilityMarkers,
    });

    const {
      modalProps,
      setModalActive,
      triggerBtnRef: addToAssignmentBtnRef,
      closeModal,
    } = useFocusRestoringModal();

    const getLabel = () => {
      if (articleType) {
        return ARTICLE_STORY_MAP[articleType as string];
      } else {
        return CONTENT_TYPES_LABELS[contentType];
      }
    };

    const getFeatureTagText = () => {
      if (languages && languages.length > 1) {
        return { text: "Español" };
      } else {
        return null;
      }
    };

    return (
      <BaseCard
        ref={ref}
        contentId={contentId}
        badges={badges}
        contentTag={getLabel() as string}
        dataQaSelector={dataQaSelector || "article-content-card"}
        detailsText={
          <>
            {formattedMaturityGradeLevelLabel ? (
              <div>
                Maturity level:{" "}
                <strong>{formattedMaturityGradeLevelLabel}</strong>
              </div>
            ) : null}
            {formattedReadingLevelLabel ? (
              <div>
                Reading level: <strong>{formattedReadingLevelLabel}</strong>
              </div>
            ) : null}
          </>
        }
        displayDate={displayDate}
        featureTag={getFeatureTagText()}
        url={url}
        images={images}
        title={title}
        specialContent={
          <>
            {isContentSettingsEnabled && (
              <AdminContentSettingsCardFooter
                contentId={contentId}
                contentSettingsGroups={contentSettingsGroups || []}
                visibilityMarkers={visibilityMarkers}
                style={contentSettingsModeCss}
              />
            )}

            <div css={cardActionsContainerCss}>
              <ButtonWithTrackEvent
                // We know that "ref" should be a Button element in
                // this case, but ButtonWithTrackEvent wraps it with a
                // generic React component.
                ref={addToAssignmentBtnRef as any}
                onClick={() => setModalActive(true)}
                flavor={Button.flavors.muted}
                size={Button.sizes.lg}
                actionName={actionNames.interact}
                objectType={objectTypes.content}
                streamProperties={{
                  content_type: "Article",
                  description:
                    "Open add to assignment modal on article content card",
                }}
                dataProps={{
                  "data-qa-selector":
                    "article-card-open-add-to-assignment-modal",
                }}
                __cssFor={addToAssignmentButtonCss}
              >
                Add to assignment
              </ButtonWithTrackEvent>

              {showAssignmentAnchor && (
                <AnchorWithTrackEvent
                  __cssFor={linkCss}
                  styleAnchorAsButton={{
                    flavor: Button.flavors.muted,
                    size: Button.sizes.lg,
                  }}
                  href={assignHref}
                  actionName={actionNames.navigate}
                  objectType={objectTypes.content}
                  streamProperties={{
                    content_type: contentType,
                    description:
                      "Navigate to create new assignment from article content card",
                  }}
                  __renderCustomLink={({ children, href, ...anchorProps }) => {
                    return (
                      <Link
                        href={typeof href === "string" ? href : ""}
                        data-qa-selector="article-card-assign-link"
                        {...anchorProps}
                        aria-label="Navigate to article to assign"
                      >
                        {children}
                      </Link>
                    );
                  }}
                >
                  Assign
                </AnchorWithTrackEvent>
              )}
            </div>

            {modalProps.isActive && (
              <AssignModal
                contentMemberIds={[contentId]}
                createAssignmentUrl={createAssignmentUrl}
                modalProps={modalProps}
                handleClickToCloseModal={closeModal}
              />
            )}
          </>
        }
      />
    );
  },
);
